import useScopedLoad from './hooks/useScopedLoad';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, Chip, CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { listScheduleViolations } from './api/tumblerApi';

const SEVERITY_COLOR = { ERROR: 'error', WARNING: 'warning' };

// IMPLEMENTATION_PLAN.md Phase 2/4 — surfaces schedule violations for a scenario with an
// error/warning filter and a link back to the offending batch, using whatever the violations
// endpoint returns (stable error codes / a formal quality gate are listed in ASSESSMENT.md as
// still missing from the backend, so this stays a plain list rather than a green/red gate).
export default function ExceptionsPanel({ scenarioId, onNavigateToBatch }) {
  const accessToken = useSelector((state) => state.auth.access_token);

  const [filter, setFilter] = useState('all');

  const loader = useCallback(async (signal) => {
    const response = await listScheduleViolations({ scenarioId, accessToken, signal });
    return Array.isArray(response.data) ? response.data : response.data.results || [];
  }, [scenarioId, accessToken]);
  const read = useScopedLoad(String(scenarioId), loader, Boolean(accessToken && scenarioId));
  const state = { ...read, results: read.data || [] };


  if (!scenarioId) return <Typography color="text.secondary">Select a scenario to see its exceptions.</Typography>;
  if (state.loading && !state.results.length) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 160 }}><CircularProgress size={28} /></Box>;
  }
  if (state.error) return <Alert severity="error">{state.error.summary}</Alert>;

  const filtered = filter === 'all' ? state.results : state.results.filter((v) => v.severity?.toLowerCase() === filter);

  return (
    <Box>
      <ToggleButtonGroup size="small" value={filter} exclusive onChange={(_, value) => value && setFilter(value)} sx={{ mb: 2 }}>
        <ToggleButton value="all">All ({state.results.length})</ToggleButton>
        <ToggleButton value="error">Errors ({state.results.filter((v) => v.severity === 'error').length})</ToggleButton>
        <ToggleButton value="warning">Warnings ({state.results.filter((v) => v.severity === 'warning').length})</ToggleButton>
      </ToggleButtonGroup>

      {!filtered.length ? (
        <Typography color="text.secondary">No {filter === 'all' ? '' : filter} exceptions for this scenario.</Typography>
      ) : (
        <Stack spacing={1}>
          {filtered.map((violation, index) => (
            <Box
              key={violation.id ?? index}
              onClick={() => violation.batch && onNavigateToBatch?.(violation.batch)}
              sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: violation.batch ? 'pointer' : 'default' }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" color={SEVERITY_COLOR[violation.severity] || 'default'} label={violation.severity || 'unknown'} />
                <Typography fontWeight={600}>{violation.violation_type || 'Violation'}</Typography>
                {violation.batch && <Typography variant="caption" color="text.secondary">Batch #{violation.batch}</Typography>}
              </Stack>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{violation.message || violation.detail || JSON.stringify(violation)}</Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
